import { ApiError, apiClient, apiFetch, bearer, unwrap } from './client';
import type { components } from './types';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '') as string;

export type LabelingIngestResponse = components['schemas']['LabelingIngestResponse'];
export type NextCardOut = components['schemas']['NextCardOut'];
export type LabelSubmitIn = components['schemas']['LabelSubmitIn'];
export type CardStatusOut = components['schemas']['CardStatusOut'];
export type DocumentListOut = components['schemas']['DocumentListOut'];
export type DocumentRowOut = components['schemas']['DocumentRowOut'];

export interface DocumentListQuery {
  file_type?: 'pdf' | 'html' | 'image';
  status?: 'pending' | 'labeled' | 'skipped' | 'unreadable';
  source?: 'labeling-tool' | 'customer-invoice';
  sort?: 'uploaded' | 'labeled';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export async function listLabelingDocuments(
  query: DocumentListQuery,
  idToken: string,
): Promise<DocumentListOut> {
  return unwrap(
    await apiClient.GET('/api/labeling/documents', {
      params: { query },
      headers: bearer(idToken),
    }),
  );
}

/** Rejects with ApiError 404 (gone) or 409 (held by someone else / not labelable). */
export async function claimCard(documentId: string, idToken: string): Promise<NextCardOut> {
  return unwrap(
    await apiClient.POST('/api/labeling/documents/{document_id}/claim', {
      params: { path: { document_id: documentId } },
      headers: bearer(idToken),
    }),
  );
}

/** Multipart upload of one or many invoices into the corpus. On the legacy
 *  `apiFetch` path — openapi-fetch doesn't compose with `FormData`. */
export async function uploadLabelingDocuments(
  files: File[],
  idToken: string,
): Promise<LabelingIngestResponse> {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  return apiFetch<LabelingIngestResponse>('/api/labeling/documents', {
    idToken,
    method: 'POST',
    body: form,
  });
}

/** Lease + fetch the next labelable card. `null` when the queue is empty. */
export async function fetchNextCard(idToken: string): Promise<NextCardOut | null> {
  return unwrap(
    await apiClient.GET('/api/labeling/next-card', { headers: bearer(idToken) }),
  );
}

export async function submitCardLabel(
  documentId: string,
  body: LabelSubmitIn,
  idToken: string,
): Promise<CardStatusOut> {
  return unwrap(
    await apiClient.POST('/api/labeling/documents/{document_id}/label', {
      params: { path: { document_id: documentId } },
      body,
      headers: bearer(idToken),
    }),
  );
}

export type SkipReason = components['schemas']['SkipIn']['reason'];

export async function skipCard(
  documentId: string,
  reason: SkipReason,
  idToken: string,
): Promise<CardStatusOut> {
  return unwrap(
    await apiClient.POST('/api/labeling/documents/{document_id}/skip', {
      params: { path: { document_id: documentId } },
      body: { reason },
      headers: bearer(idToken),
    }),
  );
}

/** Fire-and-forget lease release when the contributor navigates away. */
export async function releaseCard(documentId: string, idToken: string): Promise<void> {
  await apiClient.POST('/api/labeling/documents/{document_id}/release', {
    params: { path: { document_id: documentId } },
    headers: bearer(idToken),
  });
}

/** Fetch a card's bytes as an object URL for the preview pane. Blob endpoint
 *  stays on raw `fetch` — the Bearer token can't ride an `<img>`/`<iframe>`
 *  src, and openapi-fetch is JSON-shaped. Caller revokes the URL. */
export async function fetchCardObjectUrl(
  documentId: string,
  idToken: string,
): Promise<{ url: string; contentType: string }> {
  const res = await fetch(
    `${API_BASE_URL}/api/labeling/documents/${encodeURIComponent(documentId)}/file`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || null);
  }
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    contentType: res.headers.get('content-type') ?? 'application/octet-stream',
  };
}

export type LabelingStatsOut = components['schemas']['LabelingStatsOut'];

/** Aggregate corpus progress for the dashboard (labeled/total, per-field
 *  coverage, contributors, agreement). Aggregates only — no field values. */
export async function fetchLabelingStats(idToken: string): Promise<LabelingStatsOut> {
  return unwrap(await apiClient.GET('/api/labeling/stats', { headers: bearer(idToken) }));
}
